import React, { useState, useEffect, useMemo, useRef } from 'react';
import { getDailySessions, deleteAllItems } from '../../../../services/Reports/ReportsApi';
import {  getActiveDailySessionData } from '../../../../services/Settings/ServicesandTaxSettingsAndSessios/SessoinAndShiftsApi';
import {fetchServiceAndTaxSettings} from "../../../../services/Settings/ServicesandTaxSettingsAndSessios/ServicesAndTaxApi";
import { Box, Typography, CircularProgress, Paper, Table, TableHead, TableBody, TableRow, TableCell, Collapse, IconButton, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Select, FormControl, InputLabel, Grid, useTheme, Alert } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import * as XLSX from 'xlsx'; // ✅ إضافة مكتبة xlsx
import ReceiptIcon from "@mui/icons-material/Receipt";
import PrintIcon from "@mui/icons-material/Print";
import { printDailyReportUtils } from '../Printing/printDailyReportUtils';
import { printDailyReportUtilsShortcut } from '../Printing/DailySessionsReportShortcut/printDailyReportUtilsShortcut';
import { useFeatures } from '../../../../context/FeaturesContext';
import InvoiceDetailsModal from './InvoiceDetailsModal';
import DeletedItemsModal from './DeletedItemsModal';


const normalizeDateValue = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value.slice(0, 10);

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return "";

  const yyyy = parsedDate.getFullYear();
  const mm = String(parsedDate.getMonth() + 1).padStart(2, '0');
  const dd = String(parsedDate.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const getDeletedItemDate = (item) => normalizeDateValue(item?.order?.date || item?.created_at);

const getDeletedItemsSummary = (items) => {
  const summary = {};

  items.forEach(item => {
    const productName = item.product?.name || "غير معروف";
    const productPrice = parseFloat(item.product?.price || 0) || 0;

    if (!summary[productName]) {
      summary[productName] = {
        name: productName,
        count: 0,
        totalPrice: 0,
        items: [],
      };
    }

    summary[productName].count += 1;
    summary[productName].totalPrice += productPrice;
    summary[productName].items.push(item);
  });

  return Object.values(summary);
};

const DailySessionReports = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openRows, setOpenRows] = useState({}); // For collapse
  const [selectedSession, setSelectedSession] = useState(null);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [exportedSessionRows, setExportedSessionRows] = useState([]); // ⬅️ بيانات التصدير داخل المودال
  const exportTableRef = useRef(null); // ⬅️ مرجع للجدول
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);
  const [sessionMode, setSessionMode] = useState(null); // 'manual' | 'automatic'
  const [sessionDate, setSessionDate] = useState(null); // when manual
  const [deletedItemsData, setDeletedItemsData] = useState([]); // ⬅️ بيانات الاصناف المحذوفة
  const [deletedItemsModalOpen, setDeletedItemsModalOpen] = useState(false); // ⬅️ حالة فتح ديالوج الاصناف المحذوفة
  const [selectedDeletedItems, setSelectedDeletedItems] = useState([]); // ⬅️ الاصناف المحذوفة المختارة
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  // call feature hook (unconditional)
  const features = useFeatures();
  // helper: resolve feature flag (supports function, object, array, boolean)
  const resolveFeatureFlag = (featObj, key) => {
    if (featObj == null) return null;
    try {
      if (typeof featObj === "function") return featObj(key);
      if (typeof featObj.isEnabled === "function") return featObj.isEnabled(key);
      if (typeof featObj.hasFeature === "function") return featObj.hasFeature(key);
      if (typeof featObj.get === "function") return featObj.get(key);
      if (key in featObj) return featObj[key];
    } catch (e) {
      return null;
    }
    return null;
  };

  // interpret reports_types feature
  const rawReportsTypes = resolveFeatureFlag(features, "reports_types");
  // explicit false => whole reports page disabled
  const isReportsEnabled = rawReportsTypes === false ? false : true;

const today = useMemo(() => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }, []);

  // جلب بيانات الاصناف المحذوفة (مرة واحدة عند التحميل)
  useEffect(() => {
    const fetchDeletedItems = async () => {
      try {
        const response = await deleteAllItems();
        if (response?.data) {
          setDeletedItemsData(response.data);
        }
      } catch (err) {
        console.error("❌ فشل في جلب الاصناف المحذوفة:", err);
        // لا نرفع الخطأ كي لا يؤثر على باقي الصفحة
        setDeletedItemsData([]);
      }
    };
    
    fetchDeletedItems();
    // تحديث البيانات كل 5 دقائق
    const interval = setInterval(fetchDeletedItems, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, []);

  // تهيئة نوع اليومية وتعيين التاريخ الافتراضي للفلاتر
  React.useEffect(() => {
    const initSessionSettings = async () => {
      try {
        const resp = await fetchServiceAndTaxSettings();
        const sess =
          resp?.data?.settings?.[0]?.session ||
          resp?.session ||
          (resp?.data && resp.data.session) ||
          null;
        setSessionMode(sess);

        if (sess === "manual") {
          const branchId = localStorage.getItem("branch_id");
          if (!branchId) {
            console.warn("⚠️ لا يوجد branch_id في localStorage");
            return;
          }
          const sresp = await getActiveDailySessionData(branchId);
          const sDate = sresp?.data?.session_date || sresp?.session_date || null;
          if (sDate) {
            setSessionDate(sDate);
            setFilters(f => ({ ...f, dateFrom: sDate, dateTo: sDate }));
          }
        } else {
          // automatic => تأكد الفلتر على تاريخ اليوم
          setSessionDate(null);
          setFilters(f => ({ ...f, dateFrom: today, dateTo: today }));
        }
      } catch (err) {
        console.error("❌ فشل في جلب إعدادات اليومية:", err);
      }
    };

    initSessionSettings();
  }, [today]);

  const [filters, setFilters] = useState(() => ({
    operationType: "",
    user: "",
    bank: "",
    transactionType: "",
    shift: "",
    search: "",
    dateFrom: today,
    dateTo: today,
  }));

  // جلب الجلسات عند تغيير التواريخ المحددة
  useEffect(() => {
    const fetchSessions = async () => {
      setLoading(true);
      try {
        const data = await getDailySessions(filters.dateFrom, filters.dateTo);
        setSessions(data);
      } catch (err) {
        setError("فشل في جلب بيانات الجلسات اليومية.");
      } finally {
        setLoading(false);
      }
    };
    fetchSessions();
  }, [filters.dateFrom, filters.dateTo]);

   const filteredSessions = useMemo(() => {
    if (
      !filters.operationType &&
      !filters.user &&
      !filters.bank &&
      !filters.transactionType &&
      !filters.shift &&
      !filters.search &&
      !filters.dateFrom &&
      !filters.dateTo
    ) return sessions;

    // فلترة العمليات داخل كل جلسة
    return sessions
      .filter(session => {
        // فلتر التاريخ على مستوى اليومية
        const sessionDate = session.session_date;
        if (filters.dateFrom && sessionDate < filters.dateFrom) return false;
        if (filters.dateTo && sessionDate > filters.dateTo) return false;
        return true;
      })
      .map(session => {
        const grouped = session.grouped_transactions_by_shift || {};
        const newGrouped = {};
        Object.entries(grouped).forEach(([shiftName, txs]) => {
          let filteredTxs = txs;
          if (filters.operationType)
            filteredTxs = filteredTxs.filter(tx => tx.type === filters.operationType);
          if (filters.user)
            filteredTxs = filteredTxs.filter(tx => session.user?.name === filters.user);
          if (filters.bank)
            filteredTxs = filteredTxs.filter(tx =>
              [tx.bank?.name, tx.bank_to?.name, tx.bank_from?.name].includes(filters.bank)
            );
          if (filters.transactionType)
            filteredTxs = filteredTxs.filter(tx =>
              filters.transactionType === "فاتورة"
                ? !!tx.invoice
                : tx.type === filters.transactionType
            );
          if (filters.shift)
            filteredTxs = shiftName === filters.shift ? filteredTxs : [];
          if (filters.search) {
            const s = filters.search.toLowerCase();
            filteredTxs = filteredTxs.filter(tx =>
              (tx.id + "").includes(s) ||
              (tx.type || "").toLowerCase().includes(s) ||
              (tx.bank?.name || "").toLowerCase().includes(s) ||
              (tx.bank_to?.name || "").toLowerCase().includes(s) ||
              (tx.bank_from?.name || "").toLowerCase().includes(s) ||
              (tx.note || "").toLowerCase().includes(s) ||
              (tx.title || "").toLowerCase().includes(s) ||
              (tx.invoice?.id + "").includes(s)
            );
          }
          if (filteredTxs.length > 0) newGrouped[shiftName] = filteredTxs;
        });
        return { ...session, grouped_transactions_by_shift: newGrouped };
      })
      .filter(session =>
        Object.values(session.grouped_transactions_by_shift || {}).some(arr => arr.length > 0)
      );
  }, [sessions, filters]);


    // استخراج القيم الممكنة للفلاتر من البيانات
  const allTransactions = useMemo(() => {
    return sessions.flatMap(session =>
      Object.entries(session.grouped_transactions_by_shift || {}).flatMap(([shift, txs]) =>
        (txs || []).map(tx => ({
          ...tx,
          session,
          shiftName: shift,
        }))
      )
    );
  }, [sessions]);

  const filteredDeletedItems = useMemo(() => {
    return deletedItemsData.filter((item) => {
      const itemDate = getDeletedItemDate(item);

      if (!itemDate) return false;
      if (filters.dateFrom && itemDate < filters.dateFrom) return false;
      if (filters.dateTo && itemDate > filters.dateTo) return false;

      return true;
    });
  }, [deletedItemsData, filters.dateFrom, filters.dateTo]);

  const deletedItemsByDate = useMemo(() => {
    return filteredDeletedItems.reduce((acc, item) => {
      const itemDate = getDeletedItemDate(item);
      if (!itemDate) return acc;

      if (!acc[itemDate]) {
        acc[itemDate] = [];
      }

      acc[itemDate].push(item);
      return acc;
    }, {});
  }, [filteredDeletedItems]);
  // احسب تاريخ اليوم بصيغة yyyy-mm-dd
  

  // فلتر الحالة مع تعيين التاريخ الافتراضي عند أول تحميل
  
  // بعد تعريف جميع الـ Hooks والدوال — إن كانت الميزة معطّلة نعرض رسالة ودّية بدل الصفحة
  if (!isReportsEnabled) {
    return (
      <Box sx={{ mt: 3 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          🚫 تقارير الجلسات اليومية غير مفعّلة حالياً.
        </Alert>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            للتفعيل تواصل مع فريق الدعم الفني
          </Typography>
          <Typography variant="body2" color="text.secondary">
            يرجى التواصل مع فريق الدعم الفني لتفعيل ميزة أنواع التقارير (key: "reports_types").
          </Typography>
        </Paper>
      </Box>
    );
  }




  const operationTypes = Array.from(new Set(allTransactions.map(tx => tx.type))).filter(Boolean);
  const users = Array.from(new Set(allTransactions.map(tx => tx.session.user?.name))).filter(Boolean);
  const banks = Array.from(new Set(allTransactions.flatMap(tx => [tx.bank?.name, tx.bank_to?.name, tx.bank_from?.name]).filter(Boolean)));
  const transactionTypes = Array.from(new Set(allTransactions.map(tx => tx.invoice ? "فاتورة" : tx.type))).filter(Boolean);
  const shifts = Array.from(new Set(allTransactions.map(tx => tx.shiftName))).filter(Boolean);

  // تطبيق الفلاتر
 
  const toggleRow = (sessionId) => {
    setOpenRows((prev) => ({
      ...prev,
      [sessionId]: !prev[sessionId],
    }));
  };

  const handleOpenReportDialog = (session) => {
    setSelectedSession(session);
    setReportDialogOpen(true);
  };

  const handleCloseReportDialog = () => {
    setReportDialogOpen(false);
    setSelectedSession(null);
  };

  // زر إعادة تهيئة الفلاتر: يرجع التاريخ إلى اليوم
  const handleResetFilters = () => {
    setFilters({
      operationType: "",
      user: "",
      bank: "",
      transactionType: "",
      shift: "",
      search: "",
      dateFrom: sessionMode === "manual" && sessionDate ? sessionDate : today,
      dateTo: sessionMode === "manual" && sessionDate ? sessionDate : today,
    });
  };

  // دالة تصدير كل البيانات (كل اليوميات)
  const handleExportToExcel = () => {
    // تجهيز البيانات بشكل مسطح (Flat) مع كل التفاصيل
    const exportRows = [];
    filteredSessions.forEach((session) => {
      const grouped = session.grouped_transactions_by_shift || {};
      Object.entries(grouped).forEach(([shiftName, transactions]) => {
        transactions.forEach((tx) => {
          exportRows.push({
            "معرف الجلسة": session.session_id,
            "تاريخ الجلسة": session.session_date,
            "اسم المستخدم": session.user?.name,
            "اسم الدخول": session.user?.username,
            "نوع المستخدم": session.user?.type,
            "الحالة": session.user?.status === 1 ? "مفعل" : "غير مفعل",
            "الوردية": shiftName,
            "رقم العملية": tx.id,
            "نوع العملية": tx.type === "withdrawal"
              ? "سحب"
              : tx.type === "deposit"
              ? "إيداع"
              : tx.type === "transfer_out"
              ? "ترحيل للخارج"
              : tx.type === "transfer_in"
              ? "ترحيل للداخل"
              : tx.type === "invoice"
              ? "فاتورة"
              : tx.type,
            "المبلغ": tx.amount,
            "الخزنة": tx.bank?.name,
            "إلى خزنة": tx.bank_to?.name || "-",
            "من خزنة": tx.bank_from?.name || "-",
            "تاريخ العملية": tx.transaction_date,
            "ملاحظات": tx.note ||
              tx.withdrawal_title ||
              tx.withdrawal_description ||
              tx.deposit_title ||
              tx.deposit_description ||
              tx.title ||
              "-",
            "فاتورة": tx.invoice
              ? `فاتورة رقم ${tx.invoice.id} (${tx.invoice.total_amount} ج.م)`
              : "-",
          });
        });
      });
    });

    // إنشاء ملف إكسل
    const ws = XLSX.utils.json_to_sheet(exportRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "تقرير الجلسات اليومية");
    XLSX.writeFile(wb, "تقرير_الجلسات_اليومية.xlsx");
  };

  // دالة فتح ديالوج الاصناف المحذوفة لعرض كل التفاصيل
  const handleOpenDeletedItemsModal = (sessionDate) => {
    const normalizedSessionDate = normalizeDateValue(sessionDate);
    setSelectedDeletedItems(deletedItemsByDate[normalizedSessionDate] || []);
    setDeletedItemsModalOpen(true);
  };

  // دالة إغلاق ديالوج الاصناف المحذوفة
  const handleCloseDeletedItemsModal = () => {
    setDeletedItemsModalOpen(false);
    setSelectedDeletedItems([]);
  };

  // دالة مساعدة لعرض إجمالي الاصناف المحذوفة فقط
  const renderDeletedItemsSummary = (sessionDate) => {
    const normalizedSessionDate = normalizeDateValue(sessionDate);
    const sessionDeletedItemsSummary = getDeletedItemsSummary(
      deletedItemsByDate[normalizedSessionDate] || []
    );
    const totalCount = sessionDeletedItemsSummary.reduce((sum, item) => sum + item.count, 0);
    const totalValue = sessionDeletedItemsSummary.reduce((sum, item) => sum + item.totalPrice, 0);
    
    if (sessionDeletedItemsSummary.length === 0) {
      return "لا توجد اصناف محذوفة";
    }
    
    return (
      <Box sx={{ 
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        bgcolor: isDark ? "#1a1a2e" : "#f5f5f5",
        p: 1.5,
        borderRadius: 1,
        transition: 'all 0.2s',
        '&:hover': {
          bgcolor: isDark ? "#232334" : "#e3f2fd",
        },
      }} onClick={() => handleOpenDeletedItemsModal(sessionDate)}>
        <Typography variant="body2" sx={{ color: isDark ? "#fff" : "#232334", fontWeight: 700 }}>
           الكمية: {totalCount} -
        </Typography>
        <Typography variant="body2" sx={{ color: isDark ? "#fff" : "#232334", fontWeight: 700 }}>
           القيمة: {totalValue.toLocaleString()} ج
        </Typography>
      </Box>
    );
  };

  // دالة طباعة مختصرة - تمرير البيانات الكاملة + تجميع الفواتير حسب النوع + إجماليات الضريبة والخصم والتوصيل والخدمات + عدد المرات + الاصناف المحذوفة
  const handlePrintShortcut = () => {
    try {
      if (typeof printDailyReportUtilsShortcut === "function") {
        // حساب أنواع الفواتير لكل يومية + إجماليات الضريبة والخصم والتوصيل والخدمات + عدد المرات + الاصناف المحذوفة
        const sessionsWithInvoiceTypes = filteredSessions.map((session) => {
          const grouped = session.grouped_transactions_by_shift || {};
          const normalizedSessionDate = normalizeDateValue(session.session_date);
          const sessionDeletedItemsSummary = getDeletedItemsSummary(
            deletedItemsByDate[normalizedSessionDate] || []
          );
          const totalDeletedItemsCount = sessionDeletedItemsSummary.reduce((sum, item) => sum + item.count, 0);
          const totalDeletedItemsValue = sessionDeletedItemsSummary.reduce((sum, item) => sum + item.totalPrice, 0);
          
          // تجميع الفواتير حسب type (delivery, dine_in, etc)
          const invoicesByType = {};
          
          // إجماليات الضريبة والخصم والتوصيل والخدمات + عدد المرات
          let totalService = 0;
          let totalDeliveryService = 0;
          let totalTax = 0;
          let totalDiscount = 0;
          
          let serviceCount = 0;
          let deliveryServiceCount = 0;
          let taxCount = 0;
          let discountCount = 0;
          
          Object.values(grouped).forEach((transactions) => {
            (transactions || []).forEach((tx) => {
              // فقط بحث عن الفواتير
              if (tx.type === "invoice" && tx.invoice?.order?.order_type) {
                const orderType = tx.invoice.order.order_type;
                if (!invoicesByType[orderType]) {
                  invoicesByType[orderType] = {
                    count: 0,
                    total: 0,
                  };
                }
                invoicesByType[orderType].count += 1;
                invoicesByType[orderType].total += parseFloat(tx.amount) || 0;
                
                // حساب إجماليات الضريبة والخصم والتوصيل والخدمات من الفاتورة + عدد المرات
                const service = parseFloat(tx.invoice?.service || 0) || 0;
                const deliveryService = parseFloat(tx.invoice?.delivery_service || 0) || 0;
                const tax = parseFloat(tx.invoice?.tax || 0) || 0;
                const discount = parseFloat(tx.invoice?.discount || 0) || 0;
                
                if (service > 0) {
                  totalService += service;
                  serviceCount += 1;
                }
                if (deliveryService > 0) {
                  totalDeliveryService += deliveryService;
                  deliveryServiceCount += 1;
                }
                if (tax > 0) {
                  totalTax += tax;
                  taxCount += 1;
                }
                if (discount > 0) {
                  totalDiscount += discount;
                  discountCount += 1;
                }
              }
            });
          });

          return {
            ...session,
            invoiceTypes: invoicesByType, // إضافة تجميع أنواع الفواتير
            invoiceDetails: { // إضافة إجماليات الضريبة والخصم والتوصيل والخدمات + عدد المرات
              service: totalService,
              deliveryService: totalDeliveryService,
              tax: totalTax,
              discount: totalDiscount,
              serviceCount,
              deliveryServiceCount,
              taxCount,
              discountCount,
            },
            deletedItemsSummary: {
              items: sessionDeletedItemsSummary,
              totalCount: totalDeletedItemsCount,
              totalValue: totalDeletedItemsValue,
            },
          };
        });

        // مرّص البيانات الكاملة مع تجميع أنواع الفواتير والتفاصيل والاصناف المحذوفة
        printDailyReportUtilsShortcut(sessionsWithInvoiceTypes);
      } else {
        console.error("❌ دالة printDailyReportUtilsShortcut غير متوفرة");
        alert("⚠️ دالة الطباعة المختصرة غير متوفرة. تحقق من الاستيراد.");
      }
    } catch (err) {
      console.error("❌ خطأ في الطباعة المختصرة:", err);
      alert("❌ حدث خطأ أثناء الطباعة المختصرة");
    }
  };

  // دالة طباعة (تستخدم filteredSessions الحالية)
  const handlePrintAll = () => {
    const rows = filteredSessions || [];

    // إذا كانت الدالة متاحة، مرّر البيانات إليها واتركها تتولى عملية الطباعة
    if (typeof printDailyReportUtils === "function") {
      try {
        printDailyReportUtils(rows);
        return;
      } catch (err) {
        console.error("❌ خطأ في printDailyReportUtils:", err);
        // تابع إلى fallback إذا فشل
      }
    }
    
    const htmlParts = [];
    htmlParts.push(`<!doctype html><html><head><meta charset="utf-8"><title>تقرير الجلسات اليومية</title><style>
      body{font-family:Arial,Helvetica,sans-serif;direction:rtl;padding:20px}
      table{border-collapse:collapse;width:100%}
      th,td{border:1px solid #ddd;padding:8px;text-align:right}
      th{background:#f5f5f5}
    </style></head><body>`);
    htmlParts.push(`<h2 style="text-align:center">تقرير الجلسات اليومية (${rows.length})</h2>`);
    htmlParts.push('<table><thead><tr><th>معرف الجلسة</th><th>تاريخ الجلسة</th><th>المستخدم</th><th>اسم الدخول</th><th>عدد العمليات</th></tr></thead><tbody>');
    rows.forEach(s => {
      const grouped = s.grouped_transactions_by_shift || {};
      const total = Object.values(grouped).reduce((acc, arr) => acc + (Array.isArray(arr) ? arr.length : 0), 0);
      htmlParts.push(`<tr><td>${s.session_id}</td><td>${s.session_date}</td><td>${s.user?.name || ''}</td><td>${s.user?.username || ''}</td><td>${total}</td></tr>`);
    });
    htmlParts.push('</tbody></table></body></html>');

    const printWindow = window.open('', '_blank', 'noopener,noreferrer');
    if (!printWindow) {
      alert('تعذر فتح نافذة الطباعة. تحقق من إعدادات المنبثقات.');
      return;
    }
    printWindow.document.open();
    printWindow.document.write(htmlParts.join(''));
    printWindow.document.close();
    printWindow.focus();
    // انتظر قليلاً لضمان تحميل المحتوى قبل الطباعة
    setTimeout(() => {
      printWindow.print();
    }, 300);
  };

  // دالة تصدير بيانات جلسة واحدة فقط
  const handleExportSingleSession = (session) => {
    const exportRows = [];
    const grouped = session.grouped_transactions_by_shift || {};
    Object.entries(grouped).forEach(([shiftName, transactions]) => {
      transactions.forEach((tx) => {
        exportRows.push({
          "معرف الجلسة": session.session_id,
          "تاريخ الجلسة": session.session_date,
          "اسم المستخدم": session.user?.name,
          "اسم الدخول": session.user?.username,
          "نوع المستخدم": session.user?.type,
          "الحالة": session.user?.status === 1 ? "مفعل" : "غير مفعل",
          "الوردية": shiftName,
          "رقم العملية": tx.id,
          "نوع العملية": tx.type === "withdrawal"
            ? "سحب"
            : tx.type === "deposit"
            ? "إيداع"
            : tx.type === "transfer_out"
            ? "ترحيل للخارج"
            : tx.type === "transfer_in"
            ? "ترحيل للداخل"
            : tx.type === "invoice"
            ? "فاتورة"
            : tx.type,
          "المبلغ": tx.amount,
          "الخزنة": tx.bank?.name,
          "إلى خزنة": tx.bank_to?.name || "-",
          "من خزنة": tx.bank_from?.name || "-",
          "تاريخ العملية": tx.transaction_date,
          "ملاحظات": tx.note ||
            tx.withdrawal_title ||
            tx.withdrawal_description ||
            tx.deposit_title ||
            tx.deposit_description ||
            tx.title ||
            "-",
          "فاتورة": tx.invoice
            ? `فاتورة رقم ${tx.invoice.id} (${tx.invoice.total_amount} ج.م)`
            : "-",
        });
      });
    });

    const ws = XLSX.utils.json_to_sheet(exportRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, `تقرير_اليومية_${session.session_id}`);
    XLSX.writeFile(wb, `تقرير_اليومية_${session.session_id}.xlsx`);
  };

  // دالة تجهيز بيانات اليومية المحددة للتصدير داخل المودال
  const handleExportSingleSessionToModal = (session) => {
    const exportRows = [];
    const grouped = session.grouped_transactions_by_shift || {};
    Object.entries(grouped).forEach(([shiftName, transactions]) => {
      transactions.forEach((tx) => {
        exportRows.push({
          "معرف الجلسة": session.session_id,
          "تاريخ الجلسة": session.session_date,
          "اسم المستخدم": session.user?.name,
          "اسم الدخول": session.user?.username,
          "نوع المستخدم": session.user?.type,
          "الحالة": session.user?.status === 1 ? "مفعل" : "غير مفعل",
          "الوردية": shiftName,
          "رقم العملية": tx.id,
          "نوع العملية": tx.type === "withdrawal"
            ? "سحب"
            : tx.type === "deposit"
            ? "إيداع"
            : tx.type === "transfer_out"
            ? "ترحيل للخارج"
            : tx.type === "transfer_in"
            ? "ترحيل للداخل"
            : tx.type === "invoice"
            ? "فاتورة"
            : tx.type,
          "المبلغ": tx.amount,
          "الخزنة": tx.bank?.name,
          "إلى خزنة": tx.bank_to?.name || "-",
          "من خزنة": tx.bank_from?.name || "-",
          "تاريخ العملية": tx.transaction_date,
          "ملاحظات": tx.note ||
            tx.withdrawal_title ||
            tx.withdrawal_description ||
            tx.deposit_title ||
            tx.deposit_description ||
            tx.title ||
            "-",
          "فاتورة": tx.invoice
            ? `فاتورة رقم ${tx.invoice.id} (${tx.invoice.total_amount} ج.م)`
            : "-",
        });
      });
    });
    setExportedSessionRows(exportRows);
    setSelectedSession(session);
    setReportDialogOpen(true);
  };

  // خريطة ترجمة نوع العملية للعربي
  const operationTypeLabels = {
    withdrawal: "سحب",
    deposit: "إيداع",
    transfer_out: "ترحيل للخارج",
    transfer_in: "ترحيل للداخل",
    invoice: "فاتورة",
  };

  // حالة لعرض بيانات الفاتورة في مودال


  // عند الضغط على أيقونة الفاتورة، اعرض بيانات الفاتورة من كائن tx.invoice مباشرة
  const handleOpenInvoice = (invoiceObj) => {
    if (!invoiceObj) return;
    setInvoiceData(invoiceObj);
    setInvoiceModalOpen(true);
  };

  const handleCloseInvoiceModal = () => {
    setInvoiceModalOpen(false);
    setInvoiceData(null);
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Typography
        variant="h4"
        fontWeight={900}
        sx={{
          mb: 3,
          textAlign: "center",
          color: isDark ? "#f48fb1" : "#1976d2",
          letterSpacing: 1,
        }}
      >
        🗂️ تقارير الجلسات اليومية
      </Typography>
      {/* زر تصدير إلى إكسل */}
      <Box sx={{ mb: 2, display: "flex", justifyContent: "flex-end", gap: 2 }}>
        <Button
          variant="outlined"
          color="secondary"
          onClick={handlePrintShortcut}
          startIcon={<PrintIcon />}
          sx={{ fontWeight: 700, borderRadius: 2 }}
        >
طباعة احصائيات        </Button>
        <Button
          variant="outlined"
          color="primary"
          onClick={handlePrintAll}
          startIcon={<PrintIcon />}
          sx={{ fontWeight: 700, borderRadius: 2 }}
        >
          طباعة
        </Button>
        <Button
          variant="contained"
          color="success"
          onClick={handleExportToExcel}
          sx={{
            bgcolor: isDark ? "#43a047" : "#43a047",
            color: "#fff",
            fontWeight: 700,
            borderRadius: 2,
            "&:hover": { bgcolor: "#388e3c" },
          }}
        >
          تصدير كل البيانات Excel
        </Button>
        {selectedSession && (
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleExportSingleSessionToModal(selectedSession)}
            sx={{
              bgcolor: isDark ? "#f48fb1" : "#1976d2",
              color: isDark ? "#232334" : "#fff",
              fontWeight: 700,
              borderRadius: 2,
              "&:hover": { bgcolor: isDark ? "#ff9a9e" : "#1565c0" },
            }}
          >
            تصدير اليومية داخل المودال
          </Button>
        )}
      </Box>

      {/* واجهة الفلاتر */}
      <Paper sx={{
        p: 2, mb: 2,
        bgcolor: isDark ? "#232334" : "#f5f5f5",
        color: isDark ? "#fff" : "#232334",
        borderRadius: 3,
        boxShadow: 2,
      }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={2}>
            <FormControl fullWidth>
              <InputLabel>نوع العملية</InputLabel>
              <Select
                value={filters.operationType}
                label="نوع العملية"
                onChange={e => setFilters(f => ({ ...f, operationType: e.target.value }))}
              >
                <MenuItem value="">الكل</MenuItem>
                {operationTypes.map(type => (
                  <MenuItem key={type} value={type}>
                    {operationTypeLabels[type] || type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2}>
            <FormControl fullWidth>
              <InputLabel>المستخدم</InputLabel>
              <Select
                value={filters.user}
                label="المستخدم"
                onChange={e => setFilters(f => ({ ...f, user: e.target.value }))}
              >
                <MenuItem value="">الكل</MenuItem>
                {users.map(u => (
                  <MenuItem key={u} value={u}>{u}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2}>
            <FormControl fullWidth>
              <InputLabel>الخزنة</InputLabel>
              <Select
                value={filters.bank}
                label="الخزنة"
                onChange={e => setFilters(f => ({ ...f, bank: e.target.value }))}
              >
                <MenuItem value="">الكل</MenuItem>
                {banks.map(b => (
                  <MenuItem key={b} value={b}>{b}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2}>
            <FormControl fullWidth>
              <InputLabel>نوع المعاملة</InputLabel>
              <Select
                value={filters.transactionType}
                label="نوع المعاملة"
                onChange={e => setFilters(f => ({ ...f, transactionType: e.target.value }))}
              >
                <MenuItem value="">الكل</MenuItem>
                {transactionTypes.map(t => (
                  <MenuItem key={t} value={t}>{t}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2}>
            <FormControl fullWidth>
              <InputLabel>الشيفت</InputLabel>
              <Select
                value={filters.shift}
                label="الشيفت"
                onChange={e => setFilters(f => ({ ...f, shift: e.target.value }))}
              >
                <MenuItem value="">الكل</MenuItem>
                {shifts.map(s => (
                  <MenuItem key={s} value={s}>{s}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField
              label="بحث"
              value={filters.search}
              onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
              fullWidth
            />
          </Grid>
          {/* فلتر التاريخ من */}
          <Grid item xs={12} sm={2}>
            <TextField
              label="تاريخ من"
              type="date"
              value={filters.dateFrom}
              onChange={e => setFilters(f => ({ ...f, dateFrom: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Grid>
          {/* فلتر التاريخ إلى */}
          <Grid item xs={12} sm={2}>
            <TextField
              label="تاريخ إلى"
              type="date"
              value={filters.dateTo}
              onChange={e => setFilters(f => ({ ...f, dateTo: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <Button
              variant="outlined"
              color="secondary"
              fullWidth
              onClick={handleResetFilters}
            >
              إعادة تهيئة الفلاتر
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {loading ? (
        <CircularProgress sx={{ display: "block", mx: "auto", mt: 4 }} />
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <Paper sx={{
          mt: 2, p: 2,
          borderRadius: 4,
          boxShadow: 6,
          bgcolor: isDark ? "#232334" : "#fff",
          color: isDark ? "#fff" : "#232334",
          transition: "background 0.2s,color 0.2s",
        }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: isDark ? "#29293a" : "#f5f5f5" }}>
                <TableCell />
                <TableCell sx={{ fontWeight: 700, color: isDark ? "#f48fb1" : "#1976d2" }}>تاريخ الجلسة</TableCell>
                <TableCell sx={{ fontWeight: 700, color: isDark ? "#f48fb1" : "#1976d2" }}>اسم المستخدم</TableCell>
                <TableCell sx={{ fontWeight: 700, color: isDark ? "#f48fb1" : "#1976d2" }}>اسم الدخول</TableCell>
                <TableCell sx={{ fontWeight: 700, color: isDark ? "#f48fb1" : "#1976d2" }}>نوع المستخدم</TableCell>
                <TableCell sx={{ fontWeight: 700, color: isDark ? "#f48fb1" : "#1976d2" }}>الحالة</TableCell>
                <TableCell sx={{ fontWeight: 700, color: isDark ? "#f48fb1" : "#1976d2" }}>عدد العمليات</TableCell>
                <TableCell sx={{ fontWeight: 700, color: isDark ? "#f48fb1" : "#1976d2" }}>الاصناف المحذوفة</TableCell>
                <TableCell sx={{ fontWeight: 700, color: isDark ? "#f48fb1" : "#1976d2" }}>تقرير اليومية</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredSessions.map((session) => {
                const grouped = session.grouped_transactions_by_shift || {};
                const hasTransactions = grouped && Object.keys(grouped).length > 0;
                const totalTransactions = hasTransactions
                  ? Object.values(grouped).reduce((acc, arr) => acc + (Array.isArray(arr) ? arr.length : 0), 0)
                  : 0;
                return (
                  <React.Fragment key={session.session_id}>
                    <TableRow
                      sx={{
                        bgcolor: isDark ? "#232334" : "#fff",
                        "&:hover": { bgcolor: isDark ? "#29293a" : "#f0f4fa" },
                        transition: "background 0.2s",
                      }}
                    >
                      <TableCell>
                        {hasTransactions && totalTransactions > 0 && (
                          <IconButton size="small" onClick={() => toggleRow(session.session_id)}>
                            {openRows[session.session_id] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                          </IconButton>
                        )}
                      </TableCell>
                      <TableCell>{session.session_date}</TableCell>
                      <TableCell>{session.user?.name}</TableCell>
                      <TableCell>{session.user?.username}</TableCell>
                      <TableCell>{session.user?.type}</TableCell>
                      <TableCell>{session.user?.status === 1 ? "مفعل" : "غير مفعل"}</TableCell>
                      <TableCell>{totalTransactions}</TableCell>
                      <TableCell>
                        {renderDeletedItemsSummary(session.session_date)}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          onClick={() => handleOpenReportDialog(session)}
                        >
                          تقرير اليومية
                        </Button>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={9} sx={{ p: 0, border: 0 }}>
                        <Collapse in={openRows[session.session_id]} timeout="auto" unmountOnExit>
                          <Box sx={{ m: 2 }}>
                            {hasTransactions && totalTransactions > 0 ? (
                              Object.entries(grouped).map(([shiftName, transactions], idx) => {
                                // حساب إجمالي الفواتير
                                const totalInvoices = transactions
                                  .filter(tx => tx.type === "invoice")
                                  .reduce((sum, tx) => sum + (parseFloat(tx.amount) || 0), 0);
                                // حساب إجمالي الإيداع
                                const totalDeposits = transactions
                                  .filter(tx => tx.type === "deposit")
                                  .reduce((sum, tx) => sum + (parseFloat(tx.amount) || 0), 0);
                                // حساب إجمالي السحب
                                const totalWithdrawals = transactions
                                  .filter(tx => tx.type === "withdrawal")
                                  .reduce((sum, tx) => sum + (parseFloat(tx.amount) || 0), 0);
                                // المعادلة المطلوبة
                                const shiftTotal = totalInvoices + totalDeposits - totalWithdrawals;

                                return (
                                  <Box key={shiftName + idx} sx={{ mb: 3 }}>
                                    <Typography variant="h6" sx={{ mb: 1, color: "#1976d2" }}>
                                      الوردية: {shiftName}
                                      <span style={{
                                        marginRight: 16,
                                        color: "#43a047",
                                        fontWeight: 700,
                                        fontSize: 18,
                                      }}>
                                        {/* عرض الإجمالي المتبقي */}
                                        {" | "}
                                        الإجمالي الوردية: {shiftTotal.toLocaleString()} ج.م
                                      </span>
                                    </Typography>
                                    <Table size="small" sx={{
                                      mb: 2,
                                      bgcolor: isDark ? "#232334" : "#f9f9f9",
                                      borderRadius: 2,
                                      color: isDark ? "#fff" : "#232334",
                                    }}>
                                      <TableHead>
                                        <TableRow sx={{ bgcolor: isDark ? "#29293a" : "#f5f5f5" }}>
                                          <TableCell sx={{ color: isDark ? "#f48fb1" : "#1976d2" }}>النوع</TableCell>
                                          <TableCell sx={{ color: isDark ? "#f48fb1" : "#1976d2" }}>فاتورة</TableCell>
                                          <TableCell sx={{ color: isDark ? "#f48fb1" : "#1976d2" }}>المبلغ</TableCell>
                                          <TableCell sx={{ color: isDark ? "#f48fb1" : "#1976d2" }}>الخزنة</TableCell>
                                          <TableCell sx={{ color: isDark ? "#f48fb1" : "#1976d2" }}>إلى خزنة</TableCell>
                                          <TableCell sx={{ color: isDark ? "#f48fb1" : "#1976d2" }}>من خزنة</TableCell>
                                          <TableCell sx={{ color: isDark ? "#f48fb1" : "#1976d2" }}>المستخدم</TableCell>
                                          <TableCell sx={{ color: isDark ? "#f48fb1" : "#1976d2" }}>تاريخ العملية</TableCell>
                                          <TableCell sx={{ color: isDark ? "#f48fb1" : "#1976d2" }}>ملاحظات</TableCell>
                                          <TableCell sx={{ color: isDark ? "#f48fb1" : "#1976d2" }}>فتح الفاتورة</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {transactions.map((tx) => (
                                          <TableRow key={tx.id}>
                                            <TableCell>
                                              {tx.type === "withdrawal"
                                                ? "سحب"
                                                : tx.type === "deposit"
                                                ? "إيداع"
                                                : tx.type === "transfer_out"
                                                ? "ترحيل للخارج"
                                                : tx.type === "transfer_in"
                                                ? "ترحيل للداخل"
                                                : tx.type === "invoice"
                                                ? "فاتورة"
                                                : tx.type}
                                            </TableCell>
                                             <TableCell>
                                              {tx.invoice
                                                ? `فاتورة رقم ${tx.invoice.order_id} (${tx.invoice.total_amount} ج.م)`
                                                : "-"}
                                            </TableCell>
                                            <TableCell>{tx.amount}</TableCell>
                                            <TableCell>{tx.bank?.name}</TableCell>
                                            <TableCell>{tx.bank_to?.name || "-"}</TableCell>
                                            <TableCell>{tx.bank_from?.name || "-"}</TableCell>
                                            <TableCell>{session.user?.name}</TableCell>
                                            <TableCell>{tx.transaction_date}</TableCell>
                                            <TableCell>
                                              {tx.note ||
                                                tx.withdrawal_title ||
                                                tx.withdrawal_description ||
                                                tx.deposit_title ||
                                                tx.deposit_description ||
                                                tx.title ||
                                                "-"}
                                            </TableCell>
                                           
                                            <TableCell>
                                              {tx.invoice && (
                                                <IconButton
                                                  color="primary"
                                                  onClick={() => handleOpenInvoice(tx.invoice)}
                                                  title="فتح الفاتورة"
                                                >
                                                  <ReceiptIcon />
                                                </IconButton>
                                              )}
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </Box>
                                );
                              })
                            ) : (
                              <Typography color="text.secondary" sx={{ mb: 2 }}>
                                لا توجد عمليات في هذه الجلسة.
                              </Typography>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* Dialog لتقرير اليومية */}
      <Dialog
        open={reportDialogOpen}
        onClose={handleCloseReportDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: isDark ? "#232334" : "#fff",
            color: isDark ? "#fff" : "#232334",
            borderRadius: 4,
          }
        }}
      >
        <DialogTitle>
          تقرير اليومية رقم {selectedSession?.session_id}
          {/* زر تصدير اليومية من داخل التقرير */}
          {selectedSession && (
            <Button
              variant="outlined"
              color="success"
              size="small"
              sx={{ ml: 2 }}
              onClick={() => handleExportSingleSession(selectedSession)}
            >
              تصدير اليومية Excel
            </Button>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {selectedSession && (
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                التاريخ: {selectedSession.session_date}
              </Typography>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                المستخدم: {selectedSession.user?.name} ({selectedSession.user?.username})
              </Typography>
              {/* جدول بيانات اليومية للتصدير */}
              {exportedSessionRows.length > 0 && (
                <Box sx={{ overflowX: "auto", mb: 3 }}>
                  <Table ref={exportTableRef} size="small">
                    <TableHead>
                      <TableRow>
                        {Object.keys(exportedSessionRows[0]).map((col) => (
                          <TableCell key={col}>{col}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {exportedSessionRows.map((row, idx) => (
                        <TableRow key={idx}>
                          {Object.values(row).map((val, i) => (
                            <TableCell key={i}>{val}</TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
              {/* ...existing code for details ... */}
              {selectedSession.grouped_transactions_by_shift &&
                Object.keys(selectedSession.grouped_transactions_by_shift).length > 0 ? (
                Object.entries(selectedSession.grouped_transactions_by_shift).map(([shiftName, transactions], idx) => (
                  <Box key={shiftName + idx} sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1, color: "#1976d2" }}>
                      الوردية: {shiftName}
                    </Typography>
                    <Table size="small" sx={{ mb: 2 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell>رقم العملية</TableCell>
                          <TableCell>النوع</TableCell>
                          <TableCell>المبلغ</TableCell>
                          <TableCell>الخزنة</TableCell>
                          <TableCell>إلى خزنة</TableCell>
                          <TableCell>من خزنة</TableCell>
                          <TableCell>المستخدم</TableCell>
                          <TableCell>تاريخ العملية</TableCell>
                          <TableCell>ملاحظات</TableCell>
                          <TableCell>فاتورة</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {transactions.map((tx) => (
                          <TableRow key={tx.id}>
                            <TableCell>{tx.id}</TableCell>
                            <TableCell>
                              {tx.type === "withdrawal"
                                ? "سحب"
                                : tx.type === "deposit"
                                ? "إيداع"
                                : tx.type === "transfer_out"
                                ? "ترحيل للخارج"
                                : tx.type === "transfer_in"
                                ? "ترحيل للداخل"
                                : tx.type === "invoice"
                                ? "فاتورة"
                                : tx.type}
                            </TableCell>
                            <TableCell>{tx.amount}</TableCell>
                            <TableCell>{tx.bank?.name}</TableCell>
                            <TableCell>{tx.bank_to?.name || "-"}</TableCell>
                            <TableCell>{tx.bank_from?.name || "-"}</TableCell>
                            <TableCell>{selectedSession.user?.name}</TableCell>
                            <TableCell>{tx.transaction_date}</TableCell>
                            <TableCell>
                              {tx.note ||
                                tx.withdrawal_title ||
                                tx.withdrawal_description ||
                                tx.deposit_title ||
                                tx.deposit_description ||
                                tx.title ||
                                "-"}
                            </TableCell>
                            <TableCell>
                              {tx.invoice
                                ? `فاتورة رقم ${tx.invoice.id} (${tx.invoice.total_amount} ج.م)`
                                : "-"}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                ))
              ) : (
                <Typography color="text.secondary" sx={{ mb: 2 }}>
                  لا توجد عمليات في هذه الجلسة.
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseReportDialog} color="primary">
            إغلاق
          </Button>
        </DialogActions>
      </Dialog>

      {/* مودال عرض بيانات الفاتورة */}
      <InvoiceDetailsModal
        open={invoiceModalOpen}
        onClose={handleCloseInvoiceModal}
        invoiceData={invoiceData}
        isDark={isDark}
      />

      {/* مودال عرض الاصناف المحذوفة */}
      <DeletedItemsModal
        open={deletedItemsModalOpen}
        onClose={handleCloseDeletedItemsModal}
        deletedItems={selectedDeletedItems}
        isDark={isDark}
      />
    </Box>
  );
};

export default DailySessionReports;
